/**
 * PreviewBox - Shows blended preview of slider changes
 * 
 * Displays a preview box to the left of the sliders panel that:
 * - Renders 5 variants (0%, 25%, 50%, 75%, 100%) of the active slider
 * - Blends between nearest two variants based on current slider value
 * - Tracks intermediates and finals separately for smooth transitions
 * - Uses canvas for image blending
 */

import RenderService from './render_service.js';
import { getRenderParams } from './slider_mappings.js';
import { PhotoRenderStatus } from './photo_render_status.js';

export class PreviewBox {
  constructor(sliderManager, stateManager) {
    this.sliderManager = sliderManager;
    this.stateManager = stateManager;
    this.editorState = stateManager.getEditorState();
    this.container = null;
    this.canvas = null;
    this.ctx = null;
    this.notch = null;
    this.sliderElement = null; // Store for scroll handling
    this.activeSliderElement = null; // Track which slider wrapper is currently active for underline
    
    // Track renders for each percentage (0, 25, 50, 75, 100)
    this.renders = {
      0: { intermediates: [], final: null, cancelFn: null, currentStep: 0, totalSteps: 0, sampleEvery: 1, hasImage: false, hasError: false },
      25: { intermediates: [], final: null, cancelFn: null, currentStep: 0, totalSteps: 0, sampleEvery: 1, hasImage: false, hasError: false },
      50: { intermediates: [], final: null, cancelFn: null, currentStep: 0, totalSteps: 0, sampleEvery: 1, hasImage: false, hasError: false },
      75: { intermediates: [], final: null, cancelFn: null, currentStep: 0, totalSteps: 0, sampleEvery: 1, hasImage: false, hasError: false },
      100: { intermediates: [], final: null, cancelFn: null, currentStep: 0, totalSteps: 0, sampleEvery: 1, hasImage: false, hasError: false }
    };
    
    // State
    this.isVisible = false;
    this.mode = 'tooltip'; // 'tooltip', 'drawer-dragging', 'drawer-always'
    this.currentPhoto = null;
    this.activeSliderName = null;
    this.activeSliderConfig = null;
    this.currentSliderValue = null;
    
    // PhotoRenderStatus for showing progress
    this.renderStatus = null;
    
    // Debounce timers
    this.refreshDebounceTimer = null;
    this.refreshDebounceDelay = 300; // ms
    
    // Event handlers (bound once)
    this.scrollHandler = this.handleScroll.bind(this);
    this.settingsChangeHandler = this.handleSettingsChange.bind(this);
    this.photoChangeHandler = this.handlePhotoChange.bind(this);
    this.sliderChangeHandler = this.handleSliderChange.bind(this);
    this.pageChangeHandler = this.handlePageChange.bind(this);
    this.renderSettingsChangeHandler = this.handleRenderSettingsChange.bind(this);
    
    // Subscribe to reactive state changes
    this.editorState.addEventListener('photoChanged', this.photoChangeHandler);
    this.editorState.addEventListener('sliderChanged', this.sliderChangeHandler);
    this.editorState.addEventListener('stateChanged', this.pageChangeHandler);
    this.editorState.addEventListener('inferenceSteps', this.renderSettingsChangeHandler);
    this.editorState.addEventListener('seed', this.renderSettingsChangeHandler);
    this.editorState.addEventListener('baseShift', this.renderSettingsChangeHandler);
    this.editorState.addEventListener('maxShift', this.renderSettingsChangeHandler);
    this.editorState.addEventListener('sampleEvery', this.renderSettingsChangeHandler);
    
    // Keep old event listener for backward compatibility (will be removed later)
    document.addEventListener('photoSettingsChanged', this.settingsChangeHandler);
  }

  /**
   * Set preview mode
   */
  setMode(mode) {
    const wasVisible = this.isVisible;
    const oldMode = this.mode;
    
    // If mode changed and was visible, recreate
    if (wasVisible && oldMode !== mode) {
      const photo = this.currentPhoto;
      const config = this.activeSliderConfig;
      const element = this.sliderElement;
      this.hide(oldMode); // Pass old mode so hide knows what to clean up
      this.mode = mode; // Update mode after hide
      if (mode === 'drawer-always') {
        this.showDrawer(photo, config, element);
      }
    } else {
      this.mode = mode; // Just update mode if not visible or no change
    }
  }

  /**
   * Set underline on slider label
   */
  setSliderLabelUnderline(sliderElement, active) {
    if (!sliderElement) return;
    
    const label = sliderElement.querySelector('label');
    if (label) {
      if (active) {
        label.classList.add('underline');
      } else {
        label.classList.remove('underline');
      }
    }
  }

  /**
   * Handle scroll events to update notch position
   */
  handleScroll() {
    if (this.isVisible && this.mode === 'tooltip' && this.sliderElement) {
      this.updatePosition(this.sliderElement);
    }
  }

  /**
   * Handle settings changes (style, inference steps, seed)
   */
  handleSettingsChange(event) {
    const { photo, setting } = event.detail;
    
    // Only refresh if visible and showing the changed photo
    if (!this.isVisible || !this.currentPhoto || this.currentPhoto.id !== photo.id) {
      return;
    }
    
    // Style changes should refresh immediately (no debounce)
    if (setting === 'style') {
      console.log('[PreviewBox.handleSettingsChange] Style change, refreshing immediately');
      this.refresh();
    } else {
      // Other settings (like inference steps, seed) use debounce
      this.debouncedRefresh();
    }
  }

  /**
   * Handle photo change (from reactive state)
   */
  handlePhotoChange(event) {
    const { photo, photoId } = event.detail;
    
    // Only respond when Edit page is active
    const state = this.editorState.get();
    if (state.currentPage !== 'edit') {
      return;
    }
    
    // If in drawer-always mode, show/update the drawer with the new photo
    if (this.mode === 'drawer-always' && photo) {
      if (this.isVisible) {
        // Drawer is visible - check if photo changed
        const oldPhotoId = this.currentPhoto ? this.currentPhoto.id : null;
        if (oldPhotoId !== photo.id) {
          // Different photo - refresh (will pull fresh values)
          this.refresh();
        }
      } else {
        // Drawer not visible yet, show it with the photo
        const sliderConfig = this.activeSliderConfig || { name: 'style', label: 'Strength', min: -4, max: 4, default: 0.0, step: 0.1 };
        this.showDrawer(photo, sliderConfig, null);
      }
      return;
    }
    
    // For other modes, only refresh if visible and showing a different photo
    if (!this.isVisible) {
      return;
    }
    
    const oldPhotoId = this.currentPhoto ? this.currentPhoto.id : null;
    if (oldPhotoId !== photoId) {
      // Photo changed while preview was visible - refresh (will pull fresh values)
      this.refresh();
    }
  }
  
  /**
   * Handle slider change (from reactive state)
   */
  handleSliderChange(event) {
    const { name, value } = event.detail;
    
    // Only respond when Edit page is active
    const state = this.editorState.get();
    if (state.currentPage !== 'edit') {
      return;
    }
    
    // Only handle if preview is visible and this is the active slider
    if (!this.isVisible || !this.activeSliderName || this.activeSliderName !== name) {
      return;
    }
    
    // Update current slider value and re-render blend
    this.currentSliderValue = value;
    this.renderBlend();
  }

  /**
   * Handle page change (from reactive state)
   */
  handlePageChange(event) {
    const changes = event.detail.changes; // event.detail = { changes }
    
    // If we just switched TO the Edit page and in drawer-always mode
    if (changes.currentPage && changes.currentPage.value === 'edit' && this.mode === 'drawer-always') {
      const state = this.editorState.get();
      
      // Get the active photo
      if (state.activePhotoId) {
        // Need to get the photo object - use stateManager's photoManager
        const photoManager = this.stateManager.photoManager || this.sliderManager.photoManager;
        if (photoManager) {
          const activePhoto = photoManager.getPhotoById(state.activePhotoId);
          
          if (activePhoto) {
            // Show drawer if not already visible or if showing a different photo
            if (!this.isVisible || !this.currentPhoto || this.currentPhoto.id !== activePhoto.id) {
              const sliderConfig = this.activeSliderConfig || { name: 'style', label: 'Strength', min: -4, max: 4, default: 0.0, step: 0.1 };
              this.showDrawer(activePhoto, sliderConfig, null);
            }
          }
        }
      }
    }
  }

  /**
   * Handle render settings changes (inferenceSteps, seed)
   */
  handleRenderSettingsChange(event) {
    console.log('[PreviewBox.handleRenderSettingsChange] Render setting changed:', event.type, event.detail);
    
    // Don't refresh while slider is being dragged
    if (this.editorState.isSliderBeingDragged()) {
      console.log('[PreviewBox.handleRenderSettingsChange] Slider being dragged, ignoring');
      return;
    }
    
    // Only refresh if visible
    if (!this.isVisible) {
      console.log('[PreviewBox.handleRenderSettingsChange] Not visible, ignoring');
      return;
    }
    
    console.log('[PreviewBox.handleRenderSettingsChange] Calling debouncedRefresh');
    // Refresh renders with new settings (debounced)
    this.debouncedRefresh();
  }

  /**
   * Debounced refresh to prevent excessive re-renders
   */
  debouncedRefresh() {
    // Clear any pending refresh
    if (this.refreshDebounceTimer) {
      clearTimeout(this.refreshDebounceTimer);
    }
    
    // Schedule new refresh
    this.refreshDebounceTimer = setTimeout(() => {
      this.refreshDebounceTimer = null;
      this.refresh();
    }, this.refreshDebounceDelay);
  }

  /**
   * Refresh the preview (cancel and restart renders)
   */
  refresh() {
    if (!this.isVisible) return;
    
    console.log('[PreviewBox] Refreshing preview - canceling and restarting renders');
    this.cancelAllRenders();
    this.startRenders();
  }

  /**
   * Cleanup event listeners (call when destroying PreviewBox)
   */
  destroy() {
    // Clear any pending debounced refresh
    if (this.refreshDebounceTimer) {
      clearTimeout(this.refreshDebounceTimer);
      this.refreshDebounceTimer = null;
    }
    
    // Clean up PhotoRenderStatus
    if (this.renderStatus) {
      this.renderStatus.destroy();
      this.renderStatus = null;
    }
    
    document.removeEventListener('photoSettingsChanged', this.settingsChangeHandler);
    this.editorState.removeEventListener('photoChanged', this.photoChangeHandler);
    this.editorState.removeEventListener('sliderChanged', this.sliderChangeHandler);
    this.editorState.removeEventListener('stateChanged', this.pageChangeHandler);
    this.editorState.removeEventListener('inferenceSteps', this.renderSettingsChangeHandler);
    this.editorState.removeEventListener('seed', this.renderSettingsChangeHandler);
    this.editorState.removeEventListener('baseShift', this.renderSettingsChangeHandler);
    this.editorState.removeEventListener('maxShift', this.renderSettingsChangeHandler);
    this.editorState.removeEventListener('sampleEvery', this.renderSettingsChangeHandler);
    this.hide();
  }

  /**
   * Show the preview box for a specific slider
   */
  show(photo, sliderConfig, sliderElement) {
    // Route to appropriate method based on mode
    if (this.mode.startsWith('drawer')) {
      this.showDrawer(photo, sliderConfig, sliderElement);
    } else {
      this.showTooltip(photo, sliderConfig, sliderElement);
    }
  }

  /**
   * Show tooltip mode
   */
  showTooltip(photo, sliderConfig, sliderElement) {
    if (this.isVisible) {
      // If already showing for same slider, just update position
      if (this.activeSliderName === sliderConfig.name && this.currentPhoto?.id === photo.id) {
        this.sliderElement = sliderElement;
        this.updatePosition(sliderElement);
        return;
      }
      // Otherwise, hide and recreate
      this.hide();
    }

    // Clear any pending debounced refresh when switching sliders (immediate render, no delay)
    if (this.refreshDebounceTimer) {
      clearTimeout(this.refreshDebounceTimer);
      this.refreshDebounceTimer = null;
    }

    // Remove underline from previous active slider
    if (this.activeSliderElement) {
      this.setSliderLabelUnderline(this.activeSliderElement, false);
    }

    this.currentPhoto = photo;
    this.activeSliderName = sliderConfig.name;
    this.activeSliderConfig = sliderConfig;
    this.currentSliderValue = photo[sliderConfig.name] !== undefined ? photo[sliderConfig.name] : sliderConfig.default;
    this.sliderElement = sliderElement;
    this.activeSliderElement = sliderElement;
    
    // Add underline to new active slider
    this.setSliderLabelUnderline(this.activeSliderElement, true);
    
    // Create container
    this.createContainer('tooltip');
    
    // Position relative to slider
    this.updatePosition(sliderElement);
    
    // Add scroll listener
    const slidersContainer = document.getElementById('sliders-container');
    if (slidersContainer) {
      slidersContainer.addEventListener('scroll', this.scrollHandler);
    }
    
    // Start rendering all 5 variants
    this.startRenders();
    
    this.isVisible = true;
  }

  /**
   * Show drawer mode
   */
  showDrawer(photo, sliderConfig, sliderElement = null) {
    if (this.isVisible && this.mode.startsWith('drawer')) {
      // If already showing drawer, just update for new slider or photo
      if (this.activeSliderName === sliderConfig.name && this.currentPhoto?.id === photo.id) {
        return;
      }
      
      // Clear any pending debounced refresh when switching sliders (immediate render, no delay)
      if (this.refreshDebounceTimer) {
        clearTimeout(this.refreshDebounceTimer);
        this.refreshDebounceTimer = null;
      }
      
      // Remove underline from previous active slider
      if (this.activeSliderElement && this.activeSliderElement !== sliderElement) {
        this.setSliderLabelUnderline(this.activeSliderElement, false);
      }
      
      // Cancel old renders and start new ones
      this.cancelAllRenders();
      
      // Update to new photo/slider
      this.currentPhoto = photo;
      this.activeSliderName = sliderConfig.name;
      this.activeSliderConfig = sliderConfig;
      this.currentSliderValue = photo[sliderConfig.name] !== undefined ? photo[sliderConfig.name] : sliderConfig.default;
      this.sliderElement = sliderElement;
      this.activeSliderElement = sliderElement;
      
      // Add underline to new active slider
      this.setSliderLabelUnderline(this.activeSliderElement, true);
      
      // Start rendering all 5 variants
      this.startRenders();
    } else if (this.isVisible) {
      // Was in tooltip mode, switch to drawer
      this.hide();
      
      // Clear any pending debounced refresh when switching sliders (immediate render, no delay)
      if (this.refreshDebounceTimer) {
        clearTimeout(this.refreshDebounceTimer);
        this.refreshDebounceTimer = null;
      }
      
      // Remove underline from previous active slider
      if (this.activeSliderElement) {
        this.setSliderLabelUnderline(this.activeSliderElement, false);
      }
      
      this.currentPhoto = photo;
      this.activeSliderName = sliderConfig.name;
      this.activeSliderConfig = sliderConfig;
      this.currentSliderValue = photo[sliderConfig.name] !== undefined ? photo[sliderConfig.name] : sliderConfig.default;
      this.sliderElement = sliderElement;
      this.activeSliderElement = sliderElement;
      
      // Add underline to new active slider
      this.setSliderLabelUnderline(this.activeSliderElement, true);
      
      // Create drawer container
      this.createContainer('drawer');
      this.isVisible = true;
      
      // Start rendering all 5 variants
      this.startRenders();
    } else {
      // Not visible at all, create everything
      // Clear any pending debounced refresh when switching sliders (immediate render, no delay)
      if (this.refreshDebounceTimer) {
        clearTimeout(this.refreshDebounceTimer);
        this.refreshDebounceTimer = null;
      }
      
      // Remove underline from previous active slider
      if (this.activeSliderElement) {
        this.setSliderLabelUnderline(this.activeSliderElement, false);
      }
      
      this.currentPhoto = photo;
      this.activeSliderName = sliderConfig.name;
      this.activeSliderConfig = sliderConfig;
      this.currentSliderValue = photo[sliderConfig.name] !== undefined ? photo[sliderConfig.name] : sliderConfig.default;
      this.sliderElement = sliderElement;
      this.activeSliderElement = sliderElement;
      
      // Add underline to new active slider
      this.setSliderLabelUnderline(this.activeSliderElement, true);
      
      // Create drawer container
      this.createContainer('drawer');
      this.isVisible = true;
      
      // Start rendering all 5 variants
      this.startRenders();
    }
  }

  /**
   * Show empty drawer (no photo selected)
   */
  showEmptyDrawer() {
    if (this.isVisible) {
      // If already showing, don't recreate
      return;
    }
    
    // Create drawer container without photo
    this.createContainer('drawer');
    this.isVisible = true;
    
    // Show empty state message using renderStatus
    if (this.renderStatus && this.renderStatus.spinner) {
      this.renderStatus.spinner.innerHTML = `
        <svg class="w-16 h-16 mb-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
        </svg>
        <p class="text-center text-gray-400">No photo selected</p>
        <p class="text-center text-gray-500 text-sm mt-2">Select a photo to see the preview</p>
      `;
      if (this.renderStatus.overlay) {
        this.renderStatus.overlay.style.display = 'flex';
      }
      // Hide progress bar for empty state
      if (this.renderStatus.progressBar) {
        this.renderStatus.progressBar.style.display = 'none';
      }
    }
    
    // Slide in the drawer
    setTimeout(() => {
      if (this.container) {
        this.container.style.transform = 'translateX(0)';
      }
    }, 10);
  }

  /**
   * Cancel all active renders without hiding
   */
  cancelAllRenders() {
    const state = this.editorState.get();
    const activePhoto = state.activePhotoId;
    const cancelCount = Object.values(this.renders).filter(r => r.cancelFn).length;
    
    if (cancelCount > 0) {
      console.log(`[PreviewBox] Canceling ${cancelCount} preview renders for photo ${activePhoto}`);
    }
    
    Object.values(this.renders).forEach(render => {
      if (render.cancelFn) {
        render.cancelFn();
        render.cancelFn = null;
      }
      render.intermediates = [];
      render.final = null;
      render.currentStep = 0;
      render.hasImage = false;
      render.hasError = false;
      // totalSteps is kept as it will be set in startRenders
    });
    
    // Reset and show PhotoRenderStatus
    if (this.renderStatus) {
      // Restore standard spinner HTML if it was modified by showEmptyDrawer
      if (this.renderStatus.spinner && !this.renderStatus.spinner.querySelector('.status-text')) {
        this.renderStatus.spinner.innerHTML = `
          <svg class="animate-spin h-12 w-12 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p class="text-center status-text">Rendering preview...</p>
        `;
      }
      
      this.renderStatus.setText('Rendering preview...');
      this.renderStatus.resetProgress();
      this.renderStatus.showSpinner(100);
    }
  }

  /**
   * Hide the preview box and cancel all renders
   * @param {string} modeToCleanup - Optional mode to use for cleanup (defaults to current mode)
   */
  hide(modeToCleanup = null) {
    if (!this.isVisible) return;
    
    const cleanupMode = modeToCleanup || this.mode;
    
    // Remove scroll listener
    const slidersContainer = document.getElementById('sliders-container');
    if (slidersContainer) {
      slidersContainer.removeEventListener('scroll', this.scrollHandler);
    }
    
    // Cancel all active renders
    this.cancelAllRenders();
    
    // Clear debounce timer
    if (this.refreshDebounceTimer) {
      clearTimeout(this.refreshDebounceTimer);
      this.refreshDebounceTimer = null;
    }
    
    // Clean up PhotoRenderStatus
    if (this.renderStatus) {
      this.renderStatus.destroy();
      this.renderStatus = null;
    }
    
    // Remove container
    if (this.container) {
      // Animate out if drawer
      if (cleanupMode.startsWith('drawer')) {
        this.container.style.transition = 'width 0.2s ease-out, padding 0.2s ease-out';
        this.container.style.width = '0';
        this.container.style.padding = '0';
        setTimeout(() => {
          if (this.container) {
            this.container.remove();
            this.container = null;
            this.canvas = null;
            this.ctx = null;
            this.notch = null;
            this.progressBar = null;
            this.loaderOverlay = null;
          }
        }, 200);
      } else {
        this.container.remove();
        this.container = null;
        this.canvas = null;
        this.ctx = null;
        this.notch = null;
      }
    }
    
    // Remove underline from active slider
    if (this.activeSliderElement) {
      this.setSliderLabelUnderline(this.activeSliderElement, false);
    }
    
    this.isVisible = false;
    this.currentPhoto = null;
    this.activeSliderName = null;
    this.activeSliderConfig = null;
    this.sliderElement = null;
    this.activeSliderElement = null;
  }

  /**
   * Update slider value and re-blend
   */
  updateSliderValue(value) {
    this.currentSliderValue = value;
    this.renderBlend();
  }

  /**
   * Create the preview box container
   */
  createContainer(mode) {
    this.container = document.createElement('div');
    this.container.id = 'preview-box';
    
    if (mode === 'drawer') {
      // Drawer mode - flex item in edit page layout
      const editPage = document.getElementById('edit-page');
      if (!editPage) return;
      
      // Positioned as flex item, animates width from 0 to 532px
      this.container.className = 'bg-gray-800/95 border-l-4 border-gray-600 shadow-2xl flex items-center justify-center';
      this.container.style.width = '0'; // Start collapsed
      this.container.style.padding = '0'; // Start with no padding
      this.container.style.flexShrink = '0'; // Don't shrink
      this.container.style.transition = 'width 0.2s ease-out, padding 0.2s ease-out';
      this.container.style.overflow = 'hidden';
      
      // No notch in drawer mode
      this.notch = null;
    } else {
      // Tooltip mode - fixed positioning
      this.container.className = 'fixed bg-gray-800/95 border-4 border-gray-600 rounded-lg shadow-2xl z-50';
      this.container.style.width = '520px'; // 512 + 8px for borders
      this.container.style.height = '520px';
      this.container.style.overflow = 'visible'; // Allow notch to extend outside
      this.container.style.padding = '4px';
      
      // Create notch (triangle pointing right) - bigger
      this.notch = document.createElement('div');
      this.notch.className = 'absolute right-0 transform translate-x-full';
      this.notch.style.width = '0';
      this.notch.style.height = '0';
      this.notch.style.borderTop = '20px solid transparent';
      this.notch.style.borderBottom = '20px solid transparent';
      this.notch.style.borderLeft = '20px solid #4b5563'; // border-gray-600
      this.notch.style.marginRight = '-4px'; // Overlap with thicker border
    }
    
    // Create canvas for blending
    this.canvas = document.createElement('canvas');
    this.canvas.width = 512;
    this.canvas.height = 512;
    // In drawer mode, maintain 1:1 aspect ratio. In tooltip mode, fill the container.
    if (mode === 'drawer') {
      this.canvas.className = 'rounded-lg';
      this.canvas.style.width = '512px';
      this.canvas.style.height = '512px';
      this.canvas.style.flexShrink = '0'; // Prevent shrinking
    } else {
      this.canvas.className = 'w-full h-full rounded-lg';
    }
    this.canvas.style.overflow = 'hidden'; // Clip canvas content to rounded corners
    this.ctx = this.canvas.getContext('2d', { willReadFrequently: true });
    
    // Create wrapper for canvas and status overlay
    const canvasWrapper = document.createElement('div');
    canvasWrapper.className = 'relative';
    canvasWrapper.appendChild(this.canvas);
    
    this.container.appendChild(canvasWrapper);
    
    // Create PhotoRenderStatus for this canvas wrapper
    this.renderStatus = new PhotoRenderStatus(canvasWrapper, {
      text: 'Rendering preview...'
    });
    this.renderStatus.create();
    
    // Show status with delay (prevents flash if cached)
    this.renderStatus.showSpinner(100);
    
    // Add notch if tooltip mode
    if (this.notch) {
      this.container.appendChild(this.notch);
    }
    
    // Append to appropriate parent
    if (mode === 'drawer') {
      // Find the flex container that holds Main Image View and Sliders
      const flexContainer = document.querySelector('#edit-page > .flex-1.flex.flex-col.md\\:flex-row');
      if (flexContainer) {
        // Insert drawer before the sliders sidebar (last child)
        const slidersSidebar = flexContainer.querySelector('.md\\:w-64');
        if (slidersSidebar) {
          flexContainer.insertBefore(this.container, slidersSidebar);
        } else {
          flexContainer.appendChild(this.container);
        }
        
        // Animate in by expanding width
        setTimeout(() => {
          this.container.style.width = '532px'; // 512 + padding
          this.container.style.padding = '10px';
        }, 10);
      }
    } else {
      document.body.appendChild(this.container);
    }
  }

  /**
   * Update position relative to slider element
   */
  updatePosition(sliderElement) {
    if (!this.container || !sliderElement) return;
    
    // Find the actual slider input (the track with the white dot)
    const sliderInput = sliderElement.querySelector('.slider-input');
    if (!sliderInput) return;
    
    const sliderRect = sliderInput.getBoundingClientRect();
    const containerWidth = 512;
    const containerHeight = 512;
    const gap = 20; // Gap between box and sliders panel
    
    // Position to the left of the sliders panel
    // Find the sliders container's parent (the adjustments panel)
    const slidersContainer = document.getElementById('sliders-container');
    if (!slidersContainer) return;
    
    const slidersPanel = slidersContainer.closest('.md\\:w-64');
    if (!slidersPanel) return;
    
    const panelRect = slidersPanel.getBoundingClientRect();
    const left = panelRect.left - containerWidth - gap;
    
    // Vertically align notch with slider input's vertical center
    const sliderCenterY = sliderRect.top + sliderRect.height / 2;
    let top = sliderCenterY - containerHeight / 2;
    
    // Clamp to viewport
    const maxTop = window.innerHeight - containerHeight - 20;
    const minTop = 20;
    top = Math.max(minTop, Math.min(maxTop, top));
    
    this.container.style.left = `${left}px`;
    this.container.style.top = `${top}px`;
    
    // Position notch vertically to align with slider input
    if (this.notch) {
      const notchTop = sliderCenterY - top - 20; // 20 = half of notch height (40px total)
      const maxNotchTop = containerHeight - 40; // 40 = full notch height
      const clampedNotchTop = Math.max(0, Math.min(maxNotchTop, notchTop));
      this.notch.style.top = `${clampedNotchTop}px`;
    }
  }

  /**
   * Start rendering all 5 variants
   * Always pulls fresh values from state to avoid stale data
   */
  startRenders() {
    // Get fresh state values
    const state = this.editorState.get();
    const photoManager = this.stateManager.photoManager || this.sliderManager.photoManager;
    
    // Get the current photo from photoManager (always fresh)
    const currentPhoto = photoManager ? photoManager.getPhotoById(state.activePhotoId) : null;
    if (!currentPhoto) {
      return;
    }
    
    // Update our cached values with fresh data
    this.currentPhoto = currentPhoto;
    
    // Get current slider value from photo (fresh)
    if (!this.activeSliderName || !this.activeSliderConfig) {
      return;
    }
    
    this.currentSliderValue = currentPhoto[this.activeSliderName] !== undefined 
      ? currentPhoto[this.activeSliderName] 
      : this.activeSliderConfig.default;
    
    const percentages = [0, 25, 50, 75, 100];
    
    percentages.forEach(percentage => {
      const sliderValue = this.activeSliderConfig.min + 
        (this.activeSliderConfig.max - this.activeSliderConfig.min) * (percentage / 100);
      
      // Get render parameters with fresh state values
      const renderParams = getRenderParams(
        currentPhoto,
        this.activeSliderName,
        sliderValue,
        state.inferenceSteps,
        state.seed,
        state.sampleEvery
      );
      
      // Get the selected style from fresh photo data
      const selectedStyle = currentPhoto.render_style || 'hta';
      
      // Create settings object for RenderService
      const settings = {
        style: selectedStyle,
        resolution: 512,
        num_inference_steps: renderParams.numInferenceSteps,
        cfg_scale: renderParams.style_strength,
        control_scale: renderParams.controlScale,
        seed: renderParams.seed,
        sample_every: renderParams.sampleEvery,
        jpg_quality: renderParams.jpgQuality,
        base_shift: state.baseShift,
        max_shift: state.maxShift,
        // Include all slider parameters
        ...Object.keys(renderParams).reduce((acc, key) => {
          if (!['numInferenceSteps', 'style_strength', 'controlScale', 'seed', 'sampleEvery', 'jpgQuality'].includes(key)) {
            acc[key] = renderParams[key];
          }
          return acc;
        }, {})
      };
      
      // Store total steps and sample interval for progress calculation
      this.renders[percentage].totalSteps = renderParams.numInferenceSteps;
      this.renders[percentage].sampleEvery = renderParams.sampleEvery;
      
      console.log(`[PreviewBox] Starting preview render at ${percentage}% for photo ${currentPhoto.id}`, {
        settings,
        numInferenceSteps: renderParams.numInferenceSteps,
        sampleEvery: renderParams.sampleEvery
      });
      
      // Request render with fresh photo ID
      const cancelFn = RenderService.getPhoto(
        currentPhoto.id,
        settings,
        {
          onProgress: (imageData) => {
            this.handleProgress(percentage, imageData, false);
          },
          onError: (error) => {
            console.error(`Preview render error (${percentage}%):`, error);
            this.handleRenderError(percentage, error);
          },
          onComplete: (imageData) => {
            this.handleProgress(percentage, imageData, true);
          }
        }
      );
      
      this.renders[percentage].cancelFn = cancelFn;
    });
  }

  /**
   * Handle progress for a specific percentage render
   */
  handleProgress(percentage, imageData, isFinal) {
    const render = this.renders[percentage];
    
    if (isFinal) {
      // Store as final and clear intermediates
      render.final = imageData;
      render.intermediates = [];
      render.currentStep = render.totalSteps; // Mark as complete
      render.hasImage = true;
      
      // Check if all finals are complete
      const allFinalsComplete = Object.values(this.renders).every(r => r.final !== null);
      if (allFinalsComplete) {
        // Discard all intermediates
        Object.values(this.renders).forEach(r => {
          r.intermediates = [];
        });
        // Hide progress bar
        if (this.renderStatus) {
          this.renderStatus.hideProgressBar();
        }
      } else {
        // Update progress bar
        this.updateProgressBar();
      }
    } else {
      // Store as intermediate and increment step count
      render.intermediates.push(imageData);
      
      // Calculate current step based on intermediate count
      // Intermediates come at sample_every intervals, so step = count * sample_every
      render.currentStep = render.intermediates.length * render.sampleEvery;
      
      // Mark this render as having an image
      render.hasImage = true;
      
      // Update progress bar
      this.updateProgressBar();
    }
    
    // Hide loader once ALL renders have at least one image or have errored
    const allRendersDoneOrErrored = Object.values(this.renders).every(r => r.hasImage || r.hasError);
    if (allRendersDoneOrErrored) {
      if (this.renderStatus) {
        this.renderStatus.hideSpinner();
      }
    }
    
    // Re-render blend
    this.renderBlend();
  }

  /**
   * Handle error for a specific percentage render
   */
  handleRenderError(percentage, error) {
    const render = this.renders[percentage];
    
    // Mark this render as having an error
    render.hasError = true;
    render.cancelFn = null;
    
    // Hide loader if all renders are done (either have image or error)
    const allRendersDoneOrErrored = Object.values(this.renders).every(r => r.hasImage || r.hasError);
    if (allRendersDoneOrErrored) {
      if (this.renderStatus) {
        this.renderStatus.hideSpinner();
      }
      
      // If ALL renders have errors and none have images, show error message
      const allHaveErrors = Object.values(this.renders).every(r => r.hasError);
      const noneHaveImages = Object.values(this.renders).every(r => !r.hasImage);
      
      if (allHaveErrors && noneHaveImages) {
        if (this.renderStatus) {
          this.renderStatus.showError('Failed to load preview. Please try again.');
        }
      }
    }
  }

  /**
   * Update progress bar based on minimum step across all renders
   */
  updateProgressBar() {
    if (!this.renderStatus) return;
    
    const renders = Object.values(this.renders);
    
    // Get the minimum current step across all renders
    const minStep = Math.min(...renders.map(r => r.currentStep || 0));
    
    // Get the maximum total steps (should all be the same)
    const maxSteps = Math.max(...renders.map(r => r.totalSteps || 1));
    
    // Update progress using PhotoRenderStatus
    this.renderStatus.updateProgress(minStep, maxSteps);
  }

  /**
   * Render blended image based on current slider value
   * Pulls fresh slider value from state
   */
  async renderBlend() {
    if (!this.ctx) return;
    
    // Don't render while spinner is visible
    if (this.renderStatus && this.renderStatus.isSpinnerVisible()) return;
    
    // Get fresh slider value from state
    const state = this.editorState.get();
    const photoManager = this.stateManager.photoManager || this.sliderManager.photoManager;
    const currentPhoto = photoManager ? photoManager.getPhotoById(state.activePhotoId) : null;
    
    if (!currentPhoto || !this.activeSliderName || !this.activeSliderConfig) {
      return;
    }
    
    // Use fresh value from photo
    const value = currentPhoto[this.activeSliderName] !== undefined 
      ? currentPhoto[this.activeSliderName] 
      : this.activeSliderConfig.default;
    
    // Update cached value
    this.currentSliderValue = value;
    
    // Find the two nearest percentages to blend between
    const min = this.activeSliderConfig.min;
    const max = this.activeSliderConfig.max;
    const normalizedValue = (value - min) / (max - min); // 0 to 1
    const percentValue = normalizedValue * 100; // 0 to 100
    
    // Find lower and upper percentages
    let lowerPercent = 0;
    let upperPercent = 100;
    
    const percentages = [0, 25, 50, 75, 100];
    for (let i = 0; i < percentages.length - 1; i++) {
      if (percentValue >= percentages[i] && percentValue <= percentages[i + 1]) {
        lowerPercent = percentages[i];
        upperPercent = percentages[i + 1];
        break;
      }
    }
    
    // Calculate blend weight (0 = all lower, 1 = all upper)
    const blendWeight = (percentValue - lowerPercent) / (upperPercent - lowerPercent);
    
    // Get images to blend
    const lowerRender = this.renders[lowerPercent];
    const upperRender = this.renders[upperPercent];
    
    // Determine which images to use (prefer finals, fall back to intermediates)
    let lowerImageData = lowerRender.final;
    let upperImageData = upperRender.final;
    
    if (!lowerImageData || !upperImageData) {
      // Use intermediates if finals not available
      // Pick the min of the max available step for both
      const lowerMaxStep = lowerRender.final ? Infinity : lowerRender.intermediates.length;
      const upperMaxStep = upperRender.final ? Infinity : upperRender.intermediates.length;
      const minMaxStep = Math.min(lowerMaxStep, upperMaxStep);
      
      if (!lowerImageData && lowerRender.intermediates.length > 0) {
        const index = Math.min(minMaxStep - 1, lowerRender.intermediates.length - 1);
        if (index >= 0) {
          lowerImageData = lowerRender.intermediates[index];
        }
      }
      
      if (!upperImageData && upperRender.intermediates.length > 0) {
        const index = Math.min(minMaxStep - 1, upperRender.intermediates.length - 1);
        if (index >= 0) {
          upperImageData = upperRender.intermediates[index];
        }
      }
    }
    
    // If we don't have both images, show what we have
    if (!lowerImageData && !upperImageData) return;
    if (!lowerImageData) {
      await this.drawSingleImage(upperImageData);
      return;
    }
    if (!upperImageData) {
      await this.drawSingleImage(lowerImageData);
      return;
    }
    
    // Blend the two images
    await this.blendImages(lowerImageData, upperImageData, blendWeight);
  }

  /**
   * Draw a single image to canvas
   */
  async drawSingleImage(imageData) {
    const blob = new Blob([imageData], { type: 'image/jpeg' });
    const imageUrl = URL.createObjectURL(blob);
    
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        // Clear canvas with black background
        this.ctx.fillStyle = '#000000';
        this.ctx.fillRect(0, 0, 512, 512);
        
        // Calculate dimensions to maintain aspect ratio
        const { x, y, width, height } = this.calculateAspectRatioFit(img.width, img.height, 512, 512);
        
        // Draw image centered with aspect ratio preserved
        this.ctx.drawImage(img, x, y, width, height);
        URL.revokeObjectURL(imageUrl);
        resolve();
      };
      img.onerror = () => {
        URL.revokeObjectURL(imageUrl);
        resolve();
      };
      img.src = imageUrl;
    });
  }

  /**
   * Blend two images using canvas
   */
  async blendImages(imageData1, imageData2, weight) {
    // Convert to blobs and load images
    const blob1 = new Blob([imageData1], { type: 'image/jpeg' });
    const blob2 = new Blob([imageData2], { type: 'image/jpeg' });
    const url1 = URL.createObjectURL(blob1);
    const url2 = URL.createObjectURL(blob2);
    
    const img1 = await this.loadImage(url1);
    const img2 = await this.loadImage(url2);
    
    // Clear canvas with black background
    this.ctx.fillStyle = '#000000';
    this.ctx.fillRect(0, 0, 512, 512);
    
    // Calculate dimensions to maintain aspect ratio (use first image's dimensions)
    const { x, y, width, height } = this.calculateAspectRatioFit(img1.width, img1.height, 512, 512);
    
    // Draw first image at full opacity
    this.ctx.globalAlpha = 1;
    this.ctx.globalCompositeOperation = 'source-over';
    this.ctx.drawImage(img1, x, y, width, height);
    
    // Draw second image blended on top
    // This gives us: result = img1 * (1 - weight) + img2 * weight
    this.ctx.globalAlpha = weight;
    this.ctx.drawImage(img2, x, y, width, height);
    
    // Reset alpha and composite operation
    this.ctx.globalAlpha = 1;
    this.ctx.globalCompositeOperation = 'source-over';
    
    // Clean up
    URL.revokeObjectURL(url1);
    URL.revokeObjectURL(url2);
  }

  /**
   * Load image from URL
   */
  loadImage(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });
  }

  /**
   * Calculate dimensions to fit image within canvas while maintaining aspect ratio
   */
  calculateAspectRatioFit(srcWidth, srcHeight, maxWidth, maxHeight) {
    const ratio = Math.min(maxWidth / srcWidth, maxHeight / srcHeight);
    const width = srcWidth * ratio;
    const height = srcHeight * ratio;
    const x = (maxWidth - width) / 2;
    const y = (maxHeight - height) / 2;
    return { x, y, width, height };
  }
}

